import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppDialog } from '../../components/AppDialog';
import { useConfirm, useSaveFlow } from '../../components/confirm';
import { useNotify } from '../../components/notifications';

const BASE = '/api/v1/parameter-defaults';
const FORM_NAME = 'Parameter Default';

interface Test {
  oid: string; matrix: string | null; test: string | null; testCode: string | null; category: string | null;
  component: string | null; method: string | null; preservative: string | null; retireDate: string | null; subOut: boolean;
}
interface QcType { id: string; name: string }
interface Parameter { id: string; name: string }
/** A parameter under a QC type — `oid` is set once it is stored. */
interface Assigned { oid?: string; qcTypeId: string; parameterId: string; parameter: string }

/** G5 "Tests" popup (TestMethod_ListView_Copy) — its columns, in its order. */
const TEST_COLUMNS: AppColumn<Test>[] = [
  { field: 'matrix', header: 'Matrix', width: 110 },
  { field: 'test', header: 'Test', width: 170 },
  { field: 'testCode', header: 'TestCode', width: 100 },
  { field: 'category', header: 'Category', width: 110 },
  { field: 'component', header: 'Component', width: 110 },
  { field: 'method', header: 'Method', width: 140 },
  { field: 'preservative', header: 'Preservative', width: 110 },
  { field: 'retireDate', header: 'RetireDate', width: 100, type: 'date' },
  {
    // A read-only tick, as G5 draws it.
    field: 'subOut', header: 'SubOut', width: 80, align: 'center',
    value: (t) => (t.subOut ? 'Yes' : 'No'), text: (t) => (t.subOut ? 'Yes' : 'No'),
    render: (t) => <input type="checkbox" className="sdt-check" checked={t.subOut} disabled aria-label="SubOut" />,
  },
];
/** G5 TestMethod_QCTypes_ListView_CopyToQcType — the list Copy takes parameters from. */
const COPY_COLUMNS: AppColumn<QcType>[] = [{ field: 'name', header: 'QCType' }];
const PARAM_COLUMNS: AppColumn<Parameter>[] = [{ field: 'name', header: 'Parameter' }];
/** G5's message when the edit icon is clicked on a parameter that is not saved yet (verbatim). */
const UNSAVED_EDIT_MESSAGE =
  'A newly created record cannot be shown until it is saved. Please save it to view its details in a separate form.';
const pairKey = (a: { qcTypeId: string; parameterId: string }) => `${a.qcTypeId}|${a.parameterId}`;

/**
 * "Testparameter Edit" (legacy `gotoTestparameterEditAction` → dashboard `TestParamterEdit`).
 *
 * Step 1 lists the tests; clicking one opens step 2, where the parameters of that test are chosen
 * per QC type: the QC type list on the left (< Prev / Next > walk it), the Available parameters
 * below it and the Selected ones beside them. **Add** moves the ticked Available parameters into
 * Selected, **Remove** takes the ticked Selected ones out, and **Copy** adds every parameter
 * of the QC type picked in the copy list. Nothing is stored until **Save**, which sends all QC
 * types' changes together and then asks, as legacy does, whether to close the window.
 */
export function TestParameterEditDialog({ onClose, onSaved }: {
  onClose: () => void;
  /** Called after a save, so the page behind can reload its grids. */
  onSaved: () => void;
}) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { confirm } = useConfirm();

  const [tests, setTests] = useState<Test[]>([]);
  const [loadingTests, setLoadingTests] = useState(true);
  const [search, setSearch] = useState('');
  const [test, setTest] = useState<Test | null>(null);

  const [qcTypes, setQcTypes] = useState<QcType[]>([]);
  const [parameters, setParameters] = useState<Parameter[]>([]);
  const [stored, setStored] = useState<Assigned[]>([]);
  const [working, setWorking] = useState<Assigned[]>([]);
  const [loading, setLoading] = useState(false);
  const [qcId, setQcId] = useState<string | null>(null);
  const [copyId, setCopyId] = useState<string | null>(null);
  const [pickAvail, setPickAvail] = useState<Set<string | number>>(new Set());
  const [pickSel, setPickSel] = useState<Set<string | number>>(new Set());
  // G5 Available list has its own find box ("Enter text to search...").
  const [availSearch, setAvailSearch] = useState('');

  useEffect(() => {
    let alive = true;
    api.get<{ data?: Test[] }>(`${BASE}/tests`)
      .then((r) => { if (alive) setTests(r.data?.data ?? []); })
      .catch(() => { if (alive) notify.error('The tests could not be loaded.'); })
      .finally(() => { if (alive) setLoadingTests(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTest = useCallback(async (t: Test) => {
    setLoading(true);
    try {
      const r = await api.get<{ data?: { qcTypes?: QcType[]; assigned?: Assigned[]; parameters?: Parameter[] } }>(
        `${BASE}/tests/${t.oid}/parameters`);
      const d = r.data?.data;
      setQcTypes(d?.qcTypes ?? []);
      setParameters(d?.parameters ?? []);
      setStored(d?.assigned ?? []);
      setWorking(d?.assigned ?? []);
      setQcId((cur) => (cur && (d?.qcTypes ?? []).some((q) => q.id === cur) ? cur : d?.qcTypes?.[0]?.id ?? null));
      setCopyId(null); setPickAvail(new Set()); setPickSel(new Set());
    } catch {
      notify.error('The test parameters could not be loaded.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = (t: Test) => { setTest(t); void loadTest(t); };

  const shownTests = useMemo(() => {
    const q = search.trim().toLowerCase();
    return !q ? tests : tests.filter((t) =>
      `${t.matrix ?? ''} ${t.test ?? ''} ${t.testCode ?? ''} ${t.category ?? ''} ${t.component ?? ''} ${t.method ?? ''} ${t.preservative ?? ''}`
        .toLowerCase().includes(q));
  }, [tests, search]);

  const selected = useMemo(() => working.filter((a) => a.qcTypeId === qcId), [working, qcId]);
  const available = useMemo(() => {
    const taken = new Set(selected.map((a) => a.parameterId));
    const q = availSearch.trim().toLowerCase();
    return parameters.filter((p) => !taken.has(p.id) && (!q || p.name.toLowerCase().includes(q)));
  }, [parameters, selected, availSearch]);
  /** QC types that have parameters, other than the current one — what Copy can copy. */
  const copySources = useMemo(
    () => qcTypes.filter((q) => q.id !== qcId && working.some((a) => a.qcTypeId === q.id)), [qcTypes, qcId, working]);

  const storedKeys = useMemo(() => new Set(stored.map(pairKey)), [stored]);
  const workingKeys = useMemo(() => new Set(working.map(pairKey)), [working]);
  const adds = useMemo(() => working.filter((a) => !storedKeys.has(pairKey(a))), [working, storedKeys]);
  const removes = useMemo(() => stored.filter((a) => !workingKeys.has(pairKey(a))), [stored, workingKeys]);
  const dirty = adds.length > 0 || removes.length > 0;

  /** Puts parameters under the current QC type; one that was stored before gets its row back. */
  const assign = (params: Parameter[]) => {
    if (!qcId) return;
    setWorking((w) => {
      const have = new Set(w.map(pairKey));
      const extra = params
        .filter((p) => !have.has(`${qcId}|${p.id}`))
        .map((p) => stored.find((s) => s.qcTypeId === qcId && s.parameterId === p.id)
          ?? { qcTypeId: qcId, parameterId: p.id, parameter: p.name });
      return [...w, ...extra];
    });
  };

  const add = () => {
    if (pickAvail.size === 0) { notify.info('Select at least one parameter to add.'); return; }
    assign(parameters.filter((p) => pickAvail.has(p.id)));
    setPickAvail(new Set());
  };
  const remove = () => {
    if (pickSel.size === 0) { notify.info('Select at least one parameter to remove.'); return; }
    setWorking((w) => w.filter((a) => !(a.qcTypeId === qcId && pickSel.has(a.parameterId))));
    setPickSel(new Set());
  };
  const copyFrom = () => {
    if (!copyId) { notify.info('Select the QC type to copy from.'); return; }
    assign(working.filter((a) => a.qcTypeId === copyId).map((a) => ({ id: a.parameterId, name: a.parameter })));
  };
  const step = (by: number) => {
    const i = qcTypes.findIndex((q) => q.id === qcId);
    const next = qcTypes[i + by];
    if (next) { setQcId(next.id); setCopyId(null); setPickAvail(new Set()); setPickSel(new Set()); }
  };

  const save = () => {
    if (!test || !dirty) return;
    void runSave({
      formName: FORM_NAME,
      auditAction: 'edit',
      save: async () => {
        await api.post(`${BASE}/tests/${test.oid}/parameters/save`, {
          adds: adds.map((a) => ({ qcTypeId: a.qcTypeId, parameterId: a.parameterId })),
          removes: removes.map((a) => a.oid),
        });
      },
      successAs: 'toast',
      onSaved: () => {
        onSaved();
        void confirm({ title: 'Saved', message: 'Saved successfully. Do you want to close the popup window?' })
          .then((close) => { if (close) onClose(); else void loadTest(test); });
      },
    });
  };

  const closeGuarded = () => {
    if (!dirty) { onClose(); return; }
    void confirm({ title: 'Unsaved changes', message: 'Unsaved changes will be lost. Do you want to proceed?' })
      .then((ok) => { if (ok) onClose(); });
  };

  const qcIndex = qcTypes.findIndex((q) => q.id === qcId);
  /** G5 Selected grid (Testparameter_LookupListView_SelectedTest_Copy): checkbox · edit icon · Parameter. On a row
   *  not saved yet the icon gives G5's message; on a saved row it does nothing (owner decision 2026-10-14). */
  const assignedColumns: AppColumn<Assigned>[] = [
    {
      field: 'editIcon', header: '', width: 34, align: 'center', sortable: false, filterable: false, noExport: true,
      render: (a) => (
        <button type="button" className="tpe-edit-btn" title="Edit" tabIndex={-1}
          onClick={(e) => { e.stopPropagation(); if (!a.oid) notify.error(UNSAVED_EDIT_MESSAGE); }}>
          <i className="ti ti-edit tpe-edit-icon" aria-hidden="true" />
        </button>
      ),
    },
    { field: 'parameter', header: 'Parameter' },
  ];
  /** G5 TestMethod_QCTypes_ListView_RunType — a radio marks the run type being edited. */
  const runTypeColumns: AppColumn<QcType>[] = [
    {
      field: 'pick', header: '', width: 34, align: 'center', sortable: false, noExport: true,
      render: (q) => <input type="radio" readOnly checked={q.id === qcId} tabIndex={-1} aria-label={`Run type ${q.name}`} />,
    },
    { field: 'name', header: 'RunType' },
  ];
  const qcRowClass = (q: QcType) =>
    (q.id === qcId ? 'tpe-qc-current' : working.some((a) => a.qcTypeId === q.id) ? 'tpe-qc-has' : undefined);

  if (!test) {
    return (
      // G5's Tests popup has no footer buttons (its OK / Cancel are switched off) — the X closes it.
      <AppDialog open title="Tests" width={1180} className="dlg-center tpe-tests" maximizable onClose={onClose}>
        {/* Search on the right, as G5 places it above the Tests grid. */}
        <div className="tpe-search-row">
          <div className="list-search tpe-search" data-unsaved-ignore>
            <span className="tb-i">🔍</span>
            <input type="text" placeholder="Search…" aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
        </div>
        <AppDataGrid<Test> rows={shownTests} columns={TEST_COLUMNS} getRowId={(t) => t.oid} loading={loadingTests}
          defaultPageSize={20} hideHeaderTools onRowClick={open} emptyText="No tests" />
      </AppDialog>
    );
  }

  return (
    // G5 dashboard "Test Parameter Edit": no footer buttons — the X closes it.
    <AppDialog open width={1100} className="dlg-center" maximizable onClose={closeGuarded} submitOnEnter={false}
      title="Test Parameter Edit">
      {/* G5 layout in four equal parts: two equal columns; the top pair (RunType | QCType) and the
          bottom pair (Available | Selected) each share one height, so the frames line up. */}
      <div className="tpe">
        <div className="tpe-cell">
          <div className="tpe-bar">
            <button type="button" className="ca-btn" onClick={() => step(-1)} disabled={qcIndex <= 0}>
              &lt; Prev
            </button>
            <button type="button" className="ca-btn" onClick={() => step(1)} disabled={qcIndex < 0 || qcIndex >= qcTypes.length - 1}>
              Next &gt;
            </button>
          </div>
          <AppDataGrid<QcType> rows={qcTypes} columns={runTypeColumns} getRowId={(q) => q.id} loading={loading}
            hideFooter hideHeaderTools showFilters={false} defaultPageSize={200} autoFocusFirstRow={false}
            selectedRowId={qcId} getRowClassName={qcRowClass}
            onRowClick={(q) => { setQcId(q.id); setCopyId(null); setPickAvail(new Set()); setPickSel(new Set()); }} />
        </div>
        <div className="tpe-cell">
          <div className="tpe-bar">
            <button type="button" className="ca-btn" onClick={copyFrom} disabled={copySources.length === 0}>
              <i className="ti ti-copy" /> Copy
            </button>
            <span className="tpe-gap" />
            <button type="button" className="ca-btn" onClick={add}><span className="tb-i tb-i-green">＋</span> Add</button>
            <button type="button" className="ca-btn" onClick={remove}><span className="tb-i tb-i-red">−</span> Remove</button>
            <button type="button" className="ca-btn ca-btn-solid" onClick={save} disabled={!dirty}>
              <span className="tb-i">💾</span> Save
            </button>
          </div>
          <AppDataGrid<QcType> rows={copySources} columns={COPY_COLUMNS} getRowId={(q) => q.id} loading={loading}
            hideFooter hideHeaderTools showFilters={false} defaultPageSize={200} autoFocusFirstRow={false}
            selectedRowId={copyId} onRowClick={(q) => setCopyId(q.id)} emptyText="No data to display" />
        </div>
        <div className="tpe-cell">
          <div className="tpe-cap">Available</div>
          <div className="list-search tpe-avail-search" data-unsaved-ignore>
            <span className="tb-i">🔍</span>
            <input type="text" placeholder="Enter text to search..." aria-label="Search available parameters"
              value={availSearch} onChange={(e) => setAvailSearch(e.target.value)} />
            {availSearch && <span className="list-search-clear" onClick={() => setAvailSearch('')}>×</span>}
          </div>
          <AppDataGrid<Parameter> rows={available} columns={PARAM_COLUMNS} getRowId={(p) => p.id} loading={loading}
            hideHeaderTools defaultPageSize={200} autoFocusFirstRow={false}
            selectable selectedIds={pickAvail} onSelectionChange={setPickAvail} emptyText="No data to display" />
        </div>
        <div className="tpe-cell">
          <div className="tpe-cap">Selected{dirty ? ` — ${adds.length} to add, ${removes.length} to remove` : ''}</div>
          <AppDataGrid<Assigned> rows={selected} columns={assignedColumns} getRowId={(a) => a.parameterId} loading={loading}
            hideHeaderTools defaultPageSize={200} autoFocusFirstRow={false}
            selectable selectedIds={pickSel} onSelectionChange={setPickSel}
            getRowClassName={(a) => (a.oid ? undefined : 'sdt-row-new')} emptyText="No data to display" />
        </div>
      </div>
    </AppDialog>
  );
}
